import { ChoiceButtonInput } from '@app/app/_components/Input/ChoiceButtonInput';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { ProductDataRider } from '@app/domain/model/eshop/transport/ProductDataRider';
import { getRiderIdentifier } from '@app/app/esales/transport/_sections/helpers/getRiderIdentifier';
import { splitNumber } from '@app/lib/split-number/splitNumber';
import { useTranslations } from 'next-intl';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { ChangeEvent } from 'react';
import { generateRiderDetails } from './generateRiderDetails';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { AppParameters } from '@app/infrastructure/parameters/model/AppParameters';
import { shouldShowReadMore } from '@app/app/esales/transport/_sections/steps/AdditionalRisksStep/shouldShowReadMore';
import { getReadMoreUrl } from '@app/app/esales/transport/_sections/steps/AdditionalRisksStep/getReadMoreUrl';

interface Props {
    handleRidersCheckboxChange: (e: ChangeEvent<HTMLInputElement>) => void;
    selectedRiders: ProductDataRider[];
    availableRiders: ProductDataRider[];
}

function getRiderReadMoreInfo(
    rider: ProductDataRider,
    t: ReturnType<typeof useTranslations>,
    appParameters: AppParameters,
) {
    const effectiveCode = rider.typeCode ?? rider.premiums.risks[0]?.riskCode;

    if (!effectiveCode) {
        return { showReadMore: false, readMoreUrl: undefined };
    }

    const riderDetails = t.raw(`eshop.transport.riderDetails.${effectiveCode}`);
    const showReadMore = riderDetails?.moreInfo && shouldShowReadMore(effectiveCode);
    const readMoreUrl = showReadMore ? getReadMoreUrl(effectiveCode, appParameters) : undefined;

    return { showReadMore, readMoreUrl };
}

export function AdditionalRisksStep({ handleRidersCheckboxChange, selectedRiders, availableRiders }: Props) {
    const t = useTranslations();
    const appParameters = useAppParameters();

    const riderOptions = availableRiders.map(rider => {
        const { showReadMore, readMoreUrl } = getRiderReadMoreInfo(rider, t, appParameters);

        return {
            value: getRiderIdentifier(rider),
            label: (
                <div className="flex flex-col items-start gap-3 font-medium">
                    <div className="text-lg">{rider.riderName}</div>
                    <div className="flex flex-row items-start gap-1">
                        <Paragraph isBold size="medium">
                            {splitNumber(rider.premiums.premium).integerPart}
                        </Paragraph>
                        <Paragraph isBold size="tiny">
                            {splitNumber(rider.premiums.premium).fractionalPart}
                        </Paragraph>
                        <Paragraph isBold>€</Paragraph>
                    </div>
                    {showReadMore && (
                        <TextButton
                            target="_blank"
                            href={readMoreUrl}
                            rightAddon={<ArrowRightIcon className="size-4" />}>
                            {t('general.readMore')}
                        </TextButton>
                    )}
                    <div className="border-t border-neutral-200 pt-3 font-normal">{generateRiderDetails(rider, t)}</div>
                </div>
            ),
        };
    });

    return (
        <ChoiceButtonInput
            type="checkbox"
            name="additionalRisks"
            onChange={handleRidersCheckboxChange}
            value={selectedRiders.map(rider => getRiderIdentifier(rider))}
            options={riderOptions}
            inputContainerClassName="flex self-start mt-6"
        />
    );
}
